import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {

  Calendar,
  Shield,
  GraduationCap,
  FileText,
  CheckCircle,
  Clock,
  Download,
  ExternalLink,
  Target,
  Activity,
  X,
  MoreHorizontal,
  Edit,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { Onboarding } from '@/services/onboarding.service';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useState, useEffect } from 'react';
import { baseURL } from '@/lib/axios';
import { getSecureItem } from '@/utils/localStorage';
import { useToast } from '@/components/ui/use-toast';

interface ViewOnboardingDialogProps {
  onboarding: Onboarding | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEditOnboarding?: boolean;
  canDeleteOnboarding?: boolean;
  onEdit?: (onboarding: Onboarding) => void;
  onDelete?: (onboarding: Onboarding) => void;
  onRefresh?: () => Promise<void>;
}

const ViewOnboardingDialog: React.FC<ViewOnboardingDialogProps> = ({
  onboarding,
  open,
  onOpenChange,
  canEditOnboarding = false,
  canDeleteOnboarding = false,
  onEdit,
  onDelete }) => {
  const [currentOnboarding, setCurrentOnboarding] = useState<Onboarding | null>(onboarding);
  const [loading] = useState(false);
  const [lastUpdated] = useState<Date | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<{
    url: string;
    title: string;
    type: string;
    fileName?: string;
  } | null>(null);
  const { toast } = useToast();

  // Update current onboarding when prop changes
  useEffect(() => {
    setCurrentOnboarding(onboarding);
  }, [onboarding]);

  // Helper function to extract hash from document URL
  const getDocumentHash = (docUrl: string) => {
    if (!docUrl) return 'document.pdf';
    const parts = docUrl.split('/');
    return parts[parts.length - 1] || 'document.pdf';
  };

  // Function to get complete backend URL for documents
  const getCompleteBackendUrl = (docUrl: string) => {
    if (!docUrl) return '';
    const baseUrl = baseURL.replace('/api/v1', '');
    const hash = getDocumentHash(docUrl);
    return `${baseUrl}/uploads/${hash}`;
  };

  // Function to handle document download
  const handleDownloadDocument = async (docUrl: string, fileName?: string) => {
    if (!docUrl) {
      toast({
        title: "❌ No Document Available",
        description: "This document has not been uploaded yet",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    const backendFileName = getDocumentHash(docUrl);
    const completeBackendUrl = getCompleteBackendUrl(docUrl);

    // Show download started toast
    toast({
      title: "📁 Download Started",
      description: `Downloading: ${fileName || backendFileName}`,
      duration: 2000,
    });

    try {
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = completeBackendUrl;
      link.download = fileName || backendFileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // Add authorization header if token exists
      const token = getSecureItem('accessToken');
      if (token) {
        // Try fetch first, then fallback to direct link
        try {
          const response = await fetch(completeBackendUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/pdf,application/octet-stream,*/*',
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);

          // Create download link
          const downloadLink = document.createElement('a');
          downloadLink.href = blobUrl;
          downloadLink.download = fileName || backendFileName;
          downloadLink.style.display = 'none';

          // Trigger download
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          // Clean up
          window.URL.revokeObjectURL(blobUrl);

          toast({
            title: "✅ Download Started",
            description: `Downloading: ${fileName || backendFileName}`,
            duration: 2000,
          });
        } catch (fetchError) {
          console.error('Fetch download failed:', fetchError);
          // Fallback to direct link
          link.click();

          toast({
            title: "ℹ️ Download Link Opened",
            description: "If download doesn't start, right-click the link and 'Save as'",
            duration: 3000,
          });
        }
      } else {
        // No token, use direct link
        link.click();

        toast({
          title: "ℹ️ Download Link Opened",
          description: "If download doesn't start, right-click the link and 'Save as'",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Download failed:', error);

      // Final fallback - open in new tab
      window.open(completeBackendUrl, '_blank');
      toast({
        title: "ℹ️ Document Opened in New Tab",
        description: "Right-click and 'Save as' to download the file",
        duration: 3000,
      });
    }
  };

  // Function to handle document viewing
  const handleViewDocument = (docUrl: string, title: string, type: string, fileName?: string) => {
    if (!docUrl) {
      toast({
        title: "❌ No Document Available",
        description: "This document has not been uploaded yet",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    setSelectedDocument({ url: docUrl, title, type, fileName });
  };

  if (!currentOnboarding) return null;

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'in_progress':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'pending':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'cancelled':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getCandidateInitials = (candidate: any) => {
    if (!candidate) return 'NA';
    if (typeof candidate === 'object') {
      const firstName = candidate.firstName || candidate.name?.split(' ')[0] || '';
      const lastName = candidate.lastName || candidate.name?.split(' ')[1] || '';
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'NA';
    }
    return candidate.toString().substring(0, 2).toUpperCase();
  };

  const getCandidateName = (candidate: any) => {
    if (!candidate) return 'Unknown Candidate';
    if (typeof candidate === 'object') {
      if (candidate.firstName && candidate.lastName) {
        return `${candidate.firstName} ${candidate.lastName}`;
      }
      return candidate.name || 'Unknown Candidate';
    }
    return candidate;
  };

  const formatDateDisplay = (date: string) => {
    if (!date) return 'Not specified';
    try {
      return format(new Date(date), 'MMM dd, yyyy');
    } catch {
      return new Date(date).toLocaleDateString();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[900px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Onboarding Details</DialogTitle>
            <DialogDescription>Detailed view of the selected onboarding</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {currentOnboarding && (
                <>
                  {/* Loading indicator */}
                  {loading && (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2" />
                      <span className="text-sm text-gray-600">Loading latest data...</span>
                    </div>
                  )}

                  {currentOnboarding.status === 'cancelled' && (
                    <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <X className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-red-800">Onboarding Cancelled</h4>
                          <p className="text-sm text-red-600 mt-1">
                            This onboarding has been cancelled and cannot be updated.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Header Section */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-6 min-w-0">
                        <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                          {getCandidateInitials(currentOnboarding.candidate)}
                        </div>
                        <div className="min-w-0">
                          <h1 className="text-3xl font-bold text-gray-900 mb-1 truncate">
                            {getCandidateName(currentOnboarding.candidate)}
                          </h1>
                          <p className="text-gray-600 text-base font-medium truncate">
                            {currentOnboarding.companyEmail}
                          </p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <Badge
                              variant="outline"
                              className={`px-3 py-1 text-sm font-medium ${getStatusColor(currentOnboarding.status || 'pending')}`}
                            >
                              {currentOnboarding.status?.charAt(0).toUpperCase() + currentOnboarding.status?.slice(1) || 'Pending'}
                            </Badge>
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Calendar className="h-4 w-4" />
                              <span>Created: {formatDateDisplay(currentOnboarding.createdAt || '')}</span>
                            </div>
                            {lastUpdated && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <RefreshCw className="h-3 w-3" />
                                <span>Updated: {lastUpdated.toLocaleTimeString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 ml-auto">
                        {/* Three-dot DropdownMenu */}
                        {(canEditOnboarding || canDeleteOnboarding) && (
                          <div className="flex items-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="flex items-center gap-2 rounded-lg ml-2 shadow-sm">
                                  <MoreHorizontal className="h-5 w-5" />
                                  <span className="font-medium">Actions</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEditOnboarding && onEdit && (
                                  <DropdownMenuItem onClick={() => onEdit(currentOnboarding)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {canDeleteOnboarding && onDelete && (
                                  <DropdownMenuItem onClick={() => onDelete(currentOnboarding)} className="text-red-600">
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Progress Section */}
                  <div className="bg-green-50 rounded-xl p-6 shadow-sm border border-green-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                        <Target className="h-5 w-5 text-green-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Progress Overview</h2>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold text-gray-900">Timeline Completion</span>
                        <span className="text-lg font-bold text-green-600">{currentOnboarding.completionPercentage || 0}%</span>
                      </div>
                      <Progress value={currentOnboarding.completionPercentage || 0} className="h-3" indicatorColor="bg-green-500" />
                      <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>0%</span>
                        <span>100%</span>
                      </div>
                      <div className="text-xs text-gray-400 mt-2">
                        Based on timeline items (documentation, education,Previous Employer Documents and tasks)
                      </div>
                    </div>
                  </div>



                  {/* Identity Proofs Card */}
                  <div className="bg-purple-50 rounded-xl p-6 shadow-sm border border-purple-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Shield className="h-5 w-5 text-purple-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Identity Verification</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Shield className="h-4 w-4 text-purple-600" />
                          </div>
                          <h3 className="font-semibold text-gray-900">Aadhaar Card</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {currentOnboarding.identityProofs?.aadhaarNumber || 'Not provided'}
                        </p>
                        {currentOnboarding.identityProofs?.aadhaarDocUrl ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleViewDocument(
                              currentOnboarding.identityProofs!.aadhaarDocUrl!,
                              'Aadhaar Card',
                              'identity',
                              'aadhaar.pdf'
                            )}
                          >
                            <FileText className="h-3 w-3 mr-2" />
                            View Document
                          </Button>
                        ) : (
                          <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500 text-center">Aadhaar Document not uploaded</p>
                          </div>
                        )}
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Shield className="h-4 w-4 text-purple-600" />
                          </div>
                          <h3 className="font-semibold text-gray-900">PAN Card</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-2 font-mono">
                          {currentOnboarding.identityProofs?.panNumber || 'Not provided'}
                        </p>
                        {currentOnboarding.identityProofs?.panDocUrl ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleViewDocument(
                              currentOnboarding.identityProofs!.panDocUrl!,
                              'PAN Card',
                              'identity',
                              'pan.pdf'
                            )}
                          >
                            <FileText className="h-3 w-3 mr-2" />
                            View Document
                          </Button>
                        ) : (
                          <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500 text-center">PAN Document not uploaded</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Previous Employer Documents Card */}
                  <div className="bg-amber-50 rounded-xl p-6 shadow-sm border border-amber-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                        <FileText className="h-5 w-5 text-amber-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Previous Employer Documents</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Experience Letter */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-amber-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-4 w-4 text-amber-600" />
                          </div>
                          <h3 className="font-semibold text-gray-900">Experience Letter</h3>
                        </div>
                        {currentOnboarding.previousEmployerDocuments?.expLetterUrl ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleViewDocument(
                              currentOnboarding.previousEmployerDocuments!.expLetterUrl!,
                              'Experience Letter',
                              'employment',
                              'experience_letter.pdf'
                            )}
                          >
                            <FileText className="h-3 w-3 mr-2" />
                            View Document
                          </Button>
                        ) : (
                          <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500 text-center">Experience Letter not uploaded</p>
                          </div>
                        )}
                      </div>

                      {/* Relieving Letter */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-amber-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-4 w-4 text-amber-600" />
                          </div>
                          <h3 className="font-semibold text-gray-900">Relieving Letter</h3>
                        </div>
                        {currentOnboarding.previousEmployerDocuments?.relievingLetterUrl ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleViewDocument(
                              currentOnboarding.previousEmployerDocuments!.relievingLetterUrl!,
                              'Relieving Letter',
                              'employment',
                              'relieving_letter.pdf'
                            )}
                          >
                            <FileText className="h-3 w-3 mr-2" />
                            View Document
                          </Button>
                        ) : (
                          <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500 text-center">Relieving Letter not uploaded</p>
                          </div>
                        )}
                      </div>

                      {/* Resignation Acceptance */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-amber-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-4 w-4 text-amber-600" />
                          </div>
                          <h3 className="font-semibold text-gray-900">Resignation Acceptance</h3>
                        </div>
                        {currentOnboarding.previousEmployerDocuments?.resignationAcceptanceUrl ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleViewDocument(
                              currentOnboarding.previousEmployerDocuments!.resignationAcceptanceUrl!,
                              'Resignation Acceptance',
                              'employment',
                              'resignation_acceptance.pdf'
                            )}
                          >
                            <FileText className="h-3 w-3 mr-2" />
                            View Document
                          </Button>
                        ) : (
                          <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500 text-center">Resignation Acceptance not uploaded</p>
                          </div>
                        )}
                      </div>

                      {/* First Month Salary Slip */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-amber-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-4 w-4 text-amber-600" />
                          </div>
                          <h3 className="font-semibold text-gray-900">First Month Salary Slip</h3>
                        </div>
                        {currentOnboarding.previousEmployerDocuments?.firstMonthSalarySlipUrl ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleViewDocument(
                              currentOnboarding.previousEmployerDocuments!.firstMonthSalarySlipUrl!,
                              'First Month Salary Slip',
                              'employment',
                              'first_month_salary_slip.pdf'
                            )}
                          >
                            <FileText className="h-3 w-3 mr-2" />
                            View Document
                          </Button>
                        ) : (
                          <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500 text-center">First Month Salary Slip not uploaded</p>
                          </div>
                        )}
                      </div>

                      {/* Second Month Salary Slip */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-amber-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-4 w-4 text-amber-600" />
                          </div>
                          <h3 className="font-semibold text-gray-900">Second Month Salary Slip</h3>
                        </div>
                        {currentOnboarding.previousEmployerDocuments?.secondMonthSalarySlipUrl ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleViewDocument(
                              currentOnboarding.previousEmployerDocuments!.secondMonthSalarySlipUrl!,
                              'Second Month Salary Slip',
                              'employment',
                              'second_month_salary_slip.pdf'
                            )}
                          >
                            <FileText className="h-3 w-3 mr-2" />
                            View Document
                          </Button>
                        ) : (
                          <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500 text-center">Second Month Salary Slip not uploaded</p>
                          </div>
                        )}
                      </div>

                      {/* Third Month Salary Slip */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-amber-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-4 w-4 text-amber-600" />
                          </div>
                          <h3 className="font-semibold text-gray-900">Third Month Salary Slip</h3>
                        </div>
                        {currentOnboarding.previousEmployerDocuments?.thirdMonthSalarySlipUrl ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleViewDocument(
                              currentOnboarding.previousEmployerDocuments!.thirdMonthSalarySlipUrl!,
                              'Third Month Salary Slip',
                              'employment',
                              'third_month_salary_slip.pdf'
                            )}
                          >
                            <FileText className="h-3 w-3 mr-2" />
                            View Document
                          </Button>
                        ) : (
                          <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500 text-center">Third Month Salary Slip not uploaded</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Educational Certificates Card */}
                  <div className="bg-indigo-50 rounded-xl p-6 shadow-sm border border-indigo-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                        <GraduationCap className="h-5 w-5 text-indigo-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Educational Certificates ({currentOnboarding.educationalCertificates?.length || 0})</h2>
                    </div>
                    {currentOnboarding.educationalCertificates && currentOnboarding.educationalCertificates.length > 0 ? (
                      <div className="space-y-4">
                        {currentOnboarding.educationalCertificates.map((cert, index) => (
                          <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-indigo-200 flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-900">{cert.degree}</p>
                              <p className="text-sm text-gray-600">{cert.institution}</p>
                            </div>
                            {cert.docUrl ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewDocument(
                                  cert.docUrl!,
                                  `${cert.degree} Certificate`,
                                  'education',
                                  `${cert.degree.toLowerCase().replace(/\s+/g, '_')}.pdf`
                                )}
                              >
                                <FileText className="h-3 w-3 mr-2" />
                                View Certificate
                              </Button>
                            ) : (
                              <div className="px-3 py-1 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-500">Certificate Document not uploaded</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <GraduationCap className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">No educational certificates provided</p>
                      </div>
                    )}
                  </div>

                  {/* Tasks Card */}
                  {currentOnboarding.tasks && currentOnboarding.tasks.length > 0 && (
                    <div className="bg-red-50 rounded-xl p-6 shadow-sm border border-red-100">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                          <Activity className="h-5 w-5 text-red-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Tasks Overview</h2>
                      </div>

                      {/* Tasks by Category Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Remaining Tasks */}
                        <div className="bg-white rounded-lg p-4 shadow-sm border border-red-200">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <h3 className="font-semibold text-gray-900">Remaining Tasks</h3>
                            <Badge variant="outline" className="ml-auto text-xs bg-red-50 text-red-700 border-red-200">
                              {currentOnboarding.tasks.filter((task: any) => {
                                const status = typeof task === 'object' ? task.status : 'pending';
                                return status !== 'completed';
                              }).length}
                            </Badge>
                          </div>
                          <div className="space-y-3 max-h-64 overflow-y-auto">
                            {currentOnboarding.tasks
                              .filter((task: any) => {
                                const status = typeof task === 'object' ? task.status : 'pending';
                                return status !== 'completed';
                              })
                              .map((task: any, index) => {
                                let taskTitle = 'Unknown Task';
                                let taskStatus = 'pending';
                                let taskCategory = 'General';

                                if (typeof task === 'string') {
                                  taskTitle = `Task ${task}`;
                                } else if (task && typeof task === 'object') {
                                  if (task.taskTemplate && task.taskTemplate.title) {
                                    taskTitle = task.taskTemplate.title;
                                    taskCategory = task.taskTemplate.category || 'General';
                                  } else if (task.title) {
                                    taskTitle = task.title;
                                    taskCategory = task.category || 'General';
                                  } else if (task._id) {
                                    taskTitle = `Task ${task._id}`;
                                  }
                                  taskStatus = task.status || 'pending';
                                }

                                return (
                                  <div key={`remaining-${index}`} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-gray-900 font-medium text-sm truncate">{taskTitle}</span>
                                        <Badge variant="outline" className="text-xs shrink-0">
                                          {taskStatus}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className="text-xs text-gray-500">{taskCategory}</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            {currentOnboarding.tasks.filter((task: any) => {
                              const status = typeof task === 'object' ? task.status : 'pending';
                              return status !== 'completed';
                            }).length === 0 && (
                                <div className="text-center py-4">
                                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                                  <p className="text-sm text-gray-500">All tasks completed!</p>
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Completed Tasks */}
                        <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <h3 className="font-semibold text-gray-900">Completed Tasks</h3>
                            <Badge variant="outline" className="ml-auto text-xs bg-green-50 text-green-700 border-green-200">
                              {currentOnboarding.tasks.filter((task: any) => {
                                const status = typeof task === 'object' ? task.status : 'pending';
                                return status === 'completed';
                              }).length}
                            </Badge>
                          </div>
                          <div className="space-y-3 max-h-64 overflow-y-auto">
                            {currentOnboarding.tasks
                              .filter((task: any) => {
                                const status = typeof task === 'object' ? task.status : 'pending';
                                return status === 'completed';
                              })
                              .map((task: any, index) => {
                                let taskTitle = 'Unknown Task';
                                let taskCategory = 'General';
                                let completedDate = '';

                                if (typeof task === 'string') {
                                  taskTitle = `Task ${task}`;
                                } else if (task && typeof task === 'object') {
                                  if (task.taskTemplate && task.taskTemplate.title) {
                                    taskTitle = task.taskTemplate.title;
                                    taskCategory = task.taskTemplate.category || 'General';
                                  } else if (task.title) {
                                    taskTitle = task.title;
                                    taskCategory = task.category || 'General';
                                  } else if (task._id) {
                                    taskTitle = `Task ${task._id}`;
                                  }
                                  completedDate = task.completedAt || task.updatedAt || '';
                                }

                                return (
                                  <div key={`completed-${index}`} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-gray-900 font-medium text-sm truncate">{taskTitle}</span>
                                        <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-200 shrink-0">
                                          Completed
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className="text-xs text-gray-500">{taskCategory}</span>
                                        {completedDate && (
                                          <span className="text-xs text-gray-400">
                                            {formatDateDisplay(completedDate)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            {currentOnboarding.tasks.filter((task: any) => {
                              const status = typeof task === 'object' ? task.status : 'pending';
                              return status === 'completed';
                            }).length === 0 && (
                                <div className="text-center py-4">
                                  <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                  <p className="text-sm text-gray-500">No completed tasks yet</p>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}



                  {/* Notes Card */}
                  {currentOnboarding.notes && (
                    <div className="bg-gray-50 rounded-xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                          <FileText className="h-5 w-5 text-gray-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Additional Notes</h2>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="prose prose-sm max-w-none">
                          <div
                            className="text-gray-900 leading-relaxed"
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              lineHeight: '1.6',
                              fontFamily: 'inherit'
                            }}
                          >
                            {currentOnboarding.notes}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Viewing Dialog */}
      <Dialog open={!!selectedDocument} onOpenChange={() => setSelectedDocument(null)}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col">
          <DialogHeader className="border-b pb-4 flex-shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                {selectedDocument?.title} - {getCandidateName(currentOnboarding?.candidate)}
              </div>
              {selectedDocument && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadDocument(
                      selectedDocument.url,
                      selectedDocument.fileName
                    )}
                    className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const url = getCompleteBackendUrl(selectedDocument.url);
                      window.open(url, '_blank');
                      toast({
                        title: "ℹ️ Document Opened",
                        description: "Document opened in new tab. Right-click and 'Save as' to download.",
                        duration: 3000,
                      });
                    }}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in Tab
                  </Button>
                </div>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">Viewing uploaded document for the candidate.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-6">
            {selectedDocument && (
              <div className="space-y-6">
                {selectedDocument.url ? (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-blue-900">Document Available</h3>
                          <p className="text-sm text-blue-700">Click the link below to view or download</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <a
                          href={getCompleteBackendUrl(selectedDocument.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline break-all text-sm font-medium cursor-pointer block bg-gray-50 p-2 rounded border"
                        >
                          {selectedDocument.fileName || 'document.pdf'}
                        </a>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Document Available</h3>
                    <p className="text-gray-600">
                      No document link was provided for this item.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewOnboardingDialog;
